import { useEffect, useState } from 'react'
import { api, currentUser } from '../api/client'

// [RGM] 2026-10-04 (regla de negocio Rafael): los enlaces/entradas a TronIA
// (FAB flotante, menú Herramientas → TronIA, pestaña TronIA de Mi Cuenta) se
// muestran SOLO si:
//   - la empresa está ACTIVA (EmpresaEstado 2) con TronIA en PRUEBA o ACTIVO, o
//   - la empresa es recién registrada (6) y usa la prueba gratuita (PRUEBA) —
//     excepción aprobada para que la pruebe antes de activar la empresa;
//   - SuperAdmin: acceso global, siempre visible.
// Mora (3) y estados 0/1/4 → ocultos.
// El estado sale de /tronia/estado (una sola consulta por carga de app; se
// reutiliza la promesa en curso y el resultado queda cacheado en módulo).

interface TroniaEstadoAcceso {
  estado?: string
  empresaEstado?: number
}

let cache: TroniaEstadoAcceso | null = null
let enCurso: Promise<TroniaEstadoAcceso | null> | null = null

const cargarTroniaEstado = (): Promise<TroniaEstadoAcceso | null> => {
  // [RGM] 2026-09-17 (reporte Rafael: "la página de login se queda brincando
  // tratando de cargar"): SIN sesión no se consulta — /tronia/estado responde
  // 401, el cliente API limpia la sesión y redirige a /login… y el Login
  // volvía a consultar: bucle de recargas. El visitante del Login (FAB
  // público) no necesita el estado de TronIA.
  if (!currentUser()) return Promise.resolve(null)
  if (cache) return Promise.resolve(cache)
  if (!enCurso) {
    enCurso = api
      .get<TroniaEstadoAcceso>('/tronia/estado')
      .then((respuesta) => {
        cache = respuesta
        return respuesta
      })
      .catch(() => null)
  }
  return enCurso
}

/** ¿La empresa puede ver/usar TronIA? (regla Rafael 2026-10-04) */
export const troniaVisible = (estado: TroniaEstadoAcceso | null): boolean => {
  if (!estado) return false
  const planEstado = (estado.estado ?? '').trim()
  if (planEstado === 'SUPERADMIN') return true
  const empresaEstado = Number(estado.empresaEstado ?? 0)
  return (
    (planEstado === 'ACTIVO' || planEstado === 'PRUEBA') &&
    (empresaEstado === 2 || (empresaEstado === 6 && planEstado === 'PRUEBA'))
  )
}

/** Hook: null mientras carga; true/false cuando se conoce el estado. */
export const useTroniaVisible = (): boolean | null => {
  const [visible, setVisible] = useState<boolean | null>(cache ? troniaVisible(cache) : null)
  useEffect(() => {
    let vivo = true
    cargarTroniaEstado().then((estado) => {
      if (vivo) setVisible(troniaVisible(estado))
    })
    return () => {
      vivo = false
    }
  }, [])
  return visible
}

/**
 * Hook: estado del plan TronIA ('PRUEBA' | 'ACTIVO' | 'INACTIVO' |
 * 'SUPERADMIN'); null mientras carga.
 * [RGM] 2026-09-29 (reporte Rafael: "¿por qué se pone TronIA activo si está
 * en prueba?"): la tarjeta de Módulos y servicios necesita el ESTADO real —
 * el booleano useTroniaVisible incluye PRUEBA y hacía ver la prueba como
 * "Plan activo".
 */
export const useTroniaEstado = (): string | null => {
  const [estado, setEstado] = useState<string | null>(
    cache ? ((cache.estado ?? '').trim() || null) : null,
  )
  useEffect(() => {
    let vivo = true
    cargarTroniaEstado().then((resp) => {
      if (vivo) setEstado(resp ? ((resp.estado ?? '').trim() || null) : null)
    })
    return () => {
      vivo = false
    }
  }, [])
  return estado
}
